import { createRoute } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { adminRoute } from '../route'
import { orpc } from '@/lib/orpc-client'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { AlertTriangle, CheckCircle2, Loader2, RefreshCw, ShieldCheck } from 'lucide-react'

/**
 * Nightly project exports (PLAN-086 §6). Super-admin only, English-only like
 * the rest of /admin. Three questions this page answers: is every project
 * being exported, when did a night last go wrong, and can I get one project's
 * data right now.
 */
export const adminBackupsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/backups',
  component: AdminBackupsPage,
})

type Backups = Awaited<ReturnType<typeof orpc.admin.listBackups>>
type BackupRow = Backups['projects'][number]
type Runs = Awaited<ReturnType<typeof orpc.admin.getBackupRuns>>

function formatBytes(bytes: number): string {
  if (bytes <= 0) return '—'
  const units = ['B', 'kB', 'MB', 'GB', 'TB']
  let value = bytes
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit++
  }
  return `${value >= 10 || unit === 0 ? Math.round(value) : value.toFixed(1)} ${units[unit]}`
}

function formatAge(hours: number | null): string {
  if (hours === null) return 'never'
  if (hours < 1) return `${Math.round(hours * 60)} min ago`
  if (hours < 48) return `${Math.round(hours)} h ago`
  return `${Math.round(hours / 24)} d ago`
}

function formatTime(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toISOString().replace('T', ' ').slice(0, 16) + 'Z'
}

function StatusBadge({ status }: { status: string | null }) {
  if (!status) return <Badge variant="outline">never run</Badge>
  if (status === 'complete') return <Badge variant="secondary">complete</Badge>
  if (status === 'running') return <Badge variant="outline">running</Badge>
  return <Badge variant="destructive">{status}</Badge>
}

function AdminBackupsPage() {
  const [data, setData] = useState<Backups | null>(null)
  const [runs, setRuns] = useState<Runs | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [orgFilter, setOrgFilter] = useState<string>('all')
  const [triggering, setTriggering] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      const [backups, history] = await Promise.all([
        orpc.admin.listBackups(),
        orpc.admin.getBackupRuns({ limit: 20 }),
      ])
      setData(backups)
      setRuns(history)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load backups')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const organizations = useMemo(() => {
    const names = new Map<string, string>()
    for (const project of data?.projects ?? []) {
      if (project.organizationId) {
        names.set(project.organizationId, project.organizationName ?? project.organizationId)
      }
    }
    return [...names.entries()].sort((a, b) => a[1].localeCompare(b[1]))
  }, [data])

  const visible = useMemo(
    () =>
      (data?.projects ?? []).filter(
        (project) => orgFilter === 'all' || project.organizationId === orgFilter
      ),
    [data, orgFilter]
  )

  const staleCount = visible.filter((project) => project.stale).length

  const exportNow = async (project: BackupRow) => {
    setTriggering(project.projectId)
    setNotice(null)
    try {
      const result = await orpc.admin.triggerBackup({ projectIds: [project.projectId] })
      setNotice(`Export started for ${project.projectName} (run ${result.runId}).`)
      // The run is asynchronous — give it a moment before the first refresh.
      setTimeout(() => void load(), 3000)
    } catch (err) {
      setNotice(err instanceof Error ? err.message : 'Could not start the export')
    } finally {
      setTriggering(null)
    }
  }

  if (loading) {
    return (
      <div className="p-8 flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading backups…
      </div>
    )
  }

  return (
    <div className="p-8 max-w-6xl space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Backups</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Nightly exports to the backup bucket: 02:30 UTC, to <code>daily/</code> (kept 7 days),
            Sunday to <code>weekly/</code> (kept 91 days). A project is flagged when its last
            complete export is older than 36 hours.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void load()}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      )}
      {notice && (
        <div className="rounded-md border border-border bg-muted px-4 py-3 text-sm">{notice}</div>
      )}

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <CardTitle className="flex items-center gap-2">
                {staleCount === 0 ? (
                  <ShieldCheck className="h-5 w-5 text-emerald-600" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-destructive" />
                )}
                Projects
              </CardTitle>
              <CardDescription>
                {staleCount === 0
                  ? `${visible.length} project(s), all exported within 36 hours.`
                  : `${staleCount} of ${visible.length} project(s) have no recent complete export.`}
                {data?.lastRunAt
                  ? ` Last run ${formatTime(data.lastRunAt)} — ${data.lastRunKind ?? 'unknown'}, ${data.lastRunStatus}.`
                  : ' No run has finished yet.'}
              </CardDescription>
            </div>
            <select
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
              value={orgFilter}
              onChange={(event) => setOrgFilter(event.target.value)}
            >
              <option value="all">All organizations</option>
              {organizations.map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-muted-foreground border-b border-border">
                <th className="py-2 pr-4 font-medium">Project</th>
                <th className="py-2 pr-4 font-medium">Organization</th>
                <th className="py-2 pr-4 font-medium">Last complete</th>
                <th className="py-2 pr-4 font-medium">Kind</th>
                <th className="py-2 pr-4 font-medium text-right">Size</th>
                <th className="py-2 pr-4 font-medium text-right">Objects</th>
                <th className="py-2 pr-4 font-medium">Last attempt</th>
                <th className="py-2 pr-4 font-medium" />
              </tr>
            </thead>
            <tbody>
              {visible.map((project) => (
                <tr
                  key={project.projectId}
                  className={`border-b border-border/60 ${project.stale ? 'bg-destructive/5' : ''}`}
                >
                  <td className="py-2 pr-4">
                    <div className="font-medium">{project.projectName}</div>
                    <div className="text-xs text-muted-foreground font-mono">
                      {project.projectId}
                    </div>
                  </td>
                  <td className="py-2 pr-4 text-muted-foreground">
                    {project.organizationName ?? '—'}
                  </td>
                  <td
                    className={`py-2 pr-4 ${project.stale ? 'text-destructive font-medium' : ''}`}
                  >
                    {formatAge(project.ageHours)}
                    {project.lastCompleteAt && (
                      <div className="text-xs text-muted-foreground">
                        {formatTime(project.lastCompleteAt)}
                      </div>
                    )}
                  </td>
                  <td className="py-2 pr-4 text-muted-foreground">
                    {project.lastCompleteKind ?? '—'}
                  </td>
                  <td className="py-2 pr-4 text-right tabular-nums">
                    {formatBytes(project.bytes)}
                  </td>
                  <td className="py-2 pr-4 text-right tabular-nums">{project.objects || '—'}</td>
                  <td className="py-2 pr-4">
                    <StatusBadge status={project.lastAttemptStatus} />
                    {project.lastError && (
                      <div
                        className="text-xs text-destructive mt-1 max-w-xs truncate"
                        title={project.lastError}
                      >
                        {project.lastError}
                      </div>
                    )}
                  </td>
                  <td className="py-2 pr-4 text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={triggering === project.projectId}
                      onClick={() => void exportNow(project)}
                    >
                      {triggering === project.projectId ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        'Export now'
                      )}
                    </Button>
                  </td>
                </tr>
              ))}
              {visible.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-6 text-center text-muted-foreground">
                    No projects.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle>Recent runs</CardTitle>
          <CardDescription>
            The {runs?.runs.length ?? 0} most recent run{(runs?.runs.length ?? 0) === 1 ? '' : 's'},
            newest first. Expand one to see which projects failed and why.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {(runs?.runs ?? []).map((run) => {
            const problems = run.projects.filter((project) => project.status !== 'complete')
            return (
              <details key={run.id} className="rounded-md border border-border px-3 py-2">
                <summary className="cursor-pointer flex items-center gap-3 text-sm">
                  {run.status === 'complete' ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                  ) : run.status === 'running' ? (
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground shrink-0" />
                  ) : (
                    <AlertTriangle className="h-4 w-4 text-destructive shrink-0" />
                  )}
                  <span className="font-mono text-xs">{run.snapshotId}</span>
                  <Badge variant="outline">{run.kind}</Badge>
                  <StatusBadge status={run.status} />
                  <span className="text-muted-foreground">
                    {run.ok} ok · {run.partial} partial · {run.failed} failed of {run.projectsTotal}
                  </span>
                  <span className="text-muted-foreground ml-auto">{formatTime(run.startedAt)}</span>
                </summary>
                <div className="mt-3 space-y-1 text-sm">
                  {problems.length === 0 && (
                    <div className="text-muted-foreground">Every project completed.</div>
                  )}
                  {problems.map((project) => (
                    <div key={project.projectId} className="flex items-start gap-2">
                      <StatusBadge status={project.status} />
                      <span className="font-medium">
                        {project.projectName ?? project.projectId}
                      </span>
                      <span className="text-destructive">{project.error}</span>
                    </div>
                  ))}
                  <div className="text-xs text-muted-foreground pt-2">
                    {run.projects.length} project record(s) ·{' '}
                    {formatBytes(run.projects.reduce((sum, p) => sum + p.bytes, 0))} ·{' '}
                    {run.projects.reduce((sum, p) => sum + p.objects, 0)} objects
                  </div>
                </div>
              </details>
            )
          })}
          {(runs?.runs.length ?? 0) === 0 && (
            <div className="text-muted-foreground text-sm">No runs recorded yet.</div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
